/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment, useEffect, useState, useCallback } from 'react';
import { Grid, Stack, IconButton, InputAdornment, FormHelperText, Box, Typography } from '@mui/material';
import { useForm, Controller, useWatch } from 'react-hook-form';
import CustomOutlinedInput from 'components/cmn/CustomOutlinedInput';
import CustomInputLabel from 'components/cmn/CustomInputLabel';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import useLogin from 'hooks/useLogin';
import isEmpty from 'lodash/isEmpty';
import { useAppDispatch } from 'store';
import { closeBackdrop, openBackdrop } from 'store/backdrop/backdropSlice';
import LoginButton from 'components/login/LoginButton';
import { handleEmailValidate, handleLoginPasswordCheck } from 'libs/mindUtils';
import CustomTooltip from 'components/cmn/CustomTooltip';
import CustomBox from 'components/cmn/CustomBox';
import { useLocation, useNavigate } from 'react-router-dom';
import { postLogin } from 'store/login/loginSlice';
import { useProject } from 'hooks/useProject';

const formInfo = [
  {
    id: 'email',
    defaultValue: '',
    label: '이메일',
    placeholder: '이메일 주소를 적어주세요',
    validate: handleEmailValidate,
  },
  {
    id: 'password',
    defaultValue: '',
    label: '비밀번호',
    placeholder: '비밀번호를 적어주세요',
    validate: handleLoginPasswordCheck,
  },
];

function LoginForm(): JSX.Element {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const { info: infoData, error, reset } = useLogin();
  const { message, accessToken: checkAccessToken } = infoData;
  const { getAdminAccountProfile } = useProject();
  const { data, isLoading } = getAdminAccountProfile();
  const {
    handleSubmit,
    control,
    formState: { errors },
    trigger,
  } = useForm();

  const loginValues = useWatch({
    control,
    defaultValue: {
      email: '',
      password: '',
    },
  });

  const handleClickShowPassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const handleMoveSignUpPage = useCallback(() => {
    navigate('/admin/signup');
  }, [navigate]);

  const handleFindPassword = useCallback(() => {
    console.log('find password');
  }, []);

  useEffect(() => {
    const handleValidate = Object.keys(loginValues).filter(field => loginValues[field].length > 0);
    if (handleValidate.length > 0) {
      trigger(handleValidate);
    }
    if (isEmpty(checkAccessToken) && isEmpty(loginValues.email) && isEmpty(loginValues.password)) {
      reset();
    }
  }, [checkAccessToken, location.pathname, loginValues, reset, trigger]);

  const onSubmit = (_data: any) => {
    dispatch(openBackdrop({}));
    dispatch(postLogin(_data))
      .unwrap()
      .then(response => {
        const {
          status,
          data: { accessToken },
        } = response;

        if (status === 200 && !isEmpty(accessToken)) {
          if (!isLoading) {
            if (data?.roles?.some(obj => obj.id === 10)) {
              navigate('/admin/project/overview');
            } else {
              navigate('/manager/member-management');
            }
          }
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container>
        {formInfo.map((obj, index) => (
          <Fragment key={obj.id}>
            <Grid item xs={12} mb={index === 0 ? 2.5 : 0}>
              <Stack spacing={1.25}>
                <Controller
                  name={obj.id}
                  control={control}
                  defaultValue=""
                  rules={{ required: true, validate: obj.validate }}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <CustomInputLabel htmlFor={obj.id} label={obj.label} />
                      <CustomOutlinedInput
                        id={obj.id}
                        value={value}
                        onChange={onChange}
                        placeholder={obj.placeholder}
                        fullWidth
                        error={!!errors[obj.id]}
                        message={(errors[obj.id]?.message as string) || ''}
                        type={obj.id === 'password' && !showPassword ? 'password' : 'text'}
                        autoComplete="off"
                        endAdornment={
                          obj.id === 'password' ? (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                edge="end"
                                size="small"
                              >
                                {showPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          ) : null
                        }
                      />
                    </>
                  )}
                />
              </Stack>
            </Grid>
          </Fragment>
        ))}
        <Grid item xs={12} mt={1} mb={2} justifyContent="flex-end" sx={{ display: 'flex' }}>
          <CustomTooltip title="비밀번호 분실 시 관리자에게 문의 하세요" placement="left" arrow>
            <Box
              onClick={handleFindPassword}
              sx={{ color: 'mind.black.A10', typography: 's14w400', textDecoration: 'underline', cursor: 'pointer' }}
            >
              비밀번호 찾기
            </Box>
          </CustomTooltip>
        </Grid>
        {error && (
          <Grid item xs={12}>
            <FormHelperText error>{message}</FormHelperText>
          </Grid>
        )}
        <Grid item xs={12}>
          <LoginButton>로그인</LoginButton>
        </Grid>

        <Grid item xs={12} mt={1} justifyContent="flex-start" sx={{ display: 'flex', width: '100%' }}>
          <Stack direction="row" alignItems="center" sx={{ flex: 1, justifyContent: 'space-between' }}>
            <Typography sx={{ color: 'mind.grey.alert', typography: 's14w400' }}>계정이 없으신가요?</Typography>
            <CustomBox title="회원가입" decoration="underline" onClick={handleMoveSignUpPage} />
          </Stack>
        </Grid>
      </Grid>
    </form>
  );
}

export default LoginForm;
